import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  Injectable,
  OnDestroy,
} from '@angular/core';
import {of, Subject} from 'rxjs';
import {TargetAudienceService, UploadedFile} from './target-audience.service';
import {toPromiseOnce} from '../helpers/helpers';
import {takeUntil} from 'rxjs/operators';
import {LayoutAlertService} from 'src/app/admin/layout-alert.service';

@Component({
    selector: 'app-target-audience',
    templateUrl: './target-audience.component.html',
    styleUrls: ['./target-audience.component.scss'],
    standalone: false
})
@Injectable()
export class TargetAudienceComponent implements OnInit, OnChanges, OnDestroy {
  @Output() audienceSaved = new EventEmitter();
  @Input() predefinedAudience;
  @Input() initialData;
  @Input() readOnlyMode: boolean = false;
  @Input() closeLayoutTimeout = 0;
  @Input() showSuccessToast = true;
  @Input() modalName = 'exampleModalCenter';

  @ViewChild('modal', {static: true}) modal;
  categories = {included: {}, excluded: {}};

  non_page_selection_fields = [
    'event_attending',
    'event_following',
    'event_main_music_style_liked',
    'event_secondary_music_styles_liked',
    'event_artist_following',
    'event_users_in_area',
    'event_group_following',
    'win_game_played',
    'win_game_won',
    'product_transaction_uncompleted',
    'product_transaction_completed',
    'profile',
  ];

  page_selection = ['page_following'];

  dataSources;
  estimatedSize$;
  events$;
  eventGroups$;
  winGames$;
  products$;
  profiles$;
  pages$;
  mailingLists$;
  customAudienceGroup$;

  selectedPageType;
  addAgeRange: boolean = false;

  types = [
    {name: 'Event', value: 'event'},
    {name: 'Artist', value: 'artist'},
    {name: 'Venue', value: 'venue'},
    {name: 'Concept', value: 'concept'},
    {name: 'User', value: 'user'},
    {name: 'City', value: 'city'},
    {name: 'Music Style', value: 'music_style'},
    {name: 'Curated List', value: 'curated_list'},
    {name: 'Category', value: 'category'},
  ];

  audienceServiceMap = new Map<AudienceType, Function>([
    [AudienceType.Event, e => this.targetAudienceService.fetchEvents(e)],
    [AudienceType.EventGroup, e => this.targetAudienceService.fetchEventGroups(e)],
    [AudienceType.Profile, e => this.targetAudienceService.fetchProfiles(e)],
    [AudienceType.Product, e => this.targetAudienceService.fetchProducts(e)],
    [AudienceType.WinGame, e => this.targetAudienceService.fetchWingames(e)],
    [AudienceType.Page, e => this.targetAudienceService.fetchPages(this.selectedPageType, e)],
    [AudienceType.MailingList, e => this.targetAudienceService.fetchMailingLists(e)],
    [AudienceType.CustomGroup, e => this.targetAudienceService.fetchCustomAudienceGroup(e)],
  ]);

  private destroy$ = new Subject<void>();

  constructor(
    private targetAudienceService: TargetAudienceService,
    private layoutAlertService: LayoutAlertService,
  ) {}

  checkDistance(value) {
    if (value && !isNaN(value)) {
      return value;
    } else {
      alert('Distance must be a number.');
    }
  }

  getAgeRange(audience, slot) {
    if (slot.age_start && slot.age_end) {
      return `${slot.age_start}-${slot.age_end}`;
    } else {
      return slot;
    }
  }

  ngOnInit() {
    this.events$ = this.targetAudienceService.fetchEvents();
    this.winGames$ = this.targetAudienceService.fetchWingames();
    this.products$ = this.targetAudienceService.fetchProducts();
    this.eventGroups$ = this.targetAudienceService.fetchEventGroups();
    this.profiles$ = this.targetAudienceService.fetchProfiles();
    this.pages$ = this.targetAudienceService.fetchPages('events');
    this.mailingLists$ = this.targetAudienceService.fetchMailingLists();
    this.customAudienceGroup$ = this.targetAudienceService.fetchCustomAudienceGroup();
    this.dataSources = this.audiencesDataSource(
      this.events$,
      this.eventGroups$,
      this.winGames$,
      this.products$,
      this.profiles$,
      this.pages$,
      this.mailingLists$,
      this.customAudienceGroup$,
    );
    if (this.predefinedAudience) {
      this.categories = this.predefinedAudience;
      this.targetAudienceService.updateEstimatedSize(this.targetAudienceService.stringifiedAudience(this.categories));
    }
    this.targetAudienceService
      .getEstimatedSize()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => (this.estimatedSize$ = data));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.predefinedAudience && changes.predefinedAudience.currentValue) {
      this.predefinedAudience = changes.predefinedAudience.currentValue;
      this.categories = this.predefinedAudience;
      this.targetAudienceService.updateEstimatedSize(this.targetAudienceService.stringifiedAudience(this.categories));
    }
  }

  clearType() {
    this.selectedPageType = null;
  }

  addSlot(container, name) {
    if (name === 'age') {
      this.addAgeRange = true;
    }
    container[name] ? container[name].unshift({}) : (container[name] = [{}]);
  }

  searchEvents(e, audience) {
    const audienceType = this.classify(audience.name);
    const serviceMethod = this.audienceServiceMap.get(audienceType);

    if (serviceMethod) {
      audience.data = serviceMethod(e);
    }
  }

  classify(name: string): AudienceType | null {
    for (const type of Object.values(AudienceType)) {
      if (name.indexOf(type) !== -1) {
        return type as AudienceType;
      }
    }
    return null;
  }

  setAge(container, start, end) {
    container.shift();
    container.push({age_start: start, age_end: end});
    this.targetAudienceService.updateEstimatedSize(this.targetAudienceService.stringifiedAudience(this.categories));
    this.addAgeRange = false;
  }

  async onOptionSelected(container, id, event) {
    container[id] = typeof event === 'string' ? event : {...container[id], ...event};
    let res;
    try {
      if (typeof event.target.files[0].name.endsWith('.csv')) {
        container[id].name = event.name;
        res = await this.onFileUpload(event.target.files[0]);
        if ('status' in res && res.status !== 200) {
          container[id] = {
            error: res.error.error,
          };
          return;
        }
        container[id] = {
          name: res.name,
          data: res.count,
          src: res.src,
          id: res.id,
        };
      }
    } catch {} // try only works for files, it is near impoissible to distinguish upload from normal input change
    this.selectedPageType = null;
    this.pages$ = [];
    this.targetAudienceService.updateEstimatedSize(this.targetAudienceService.stringifiedAudience(this.categories));
  }

  removeSelected(container, i) {
    if (typeof container[i] !== 'string' && 'src' in container[i]) {
      this.deleteCsv(container[i].id);
    }
    container.splice(i, 1);
    this.targetAudienceService.updateEstimatedSize(this.targetAudienceService.stringifiedAudience(this.categories));
  }

  deleteCsv(id) {
    return toPromiseOnce(this.targetAudienceService.deleteCsv(id));
  }

  onSave() {
    const data = this.targetAudienceService.stringifiedAudience(this.categories);
    this.audienceSaved.emit(data);
    if (this.showSuccessToast) {
      this.layoutAlertService.success('Audience updated successfully', this.closeLayoutTimeout);
    }
  }

  clearPageType() {
    this.selectedPageType = null;
  }

  audiencesDataSource = (
    events$,
    eventGroups$,
    winGames$,
    products$,
    profiles$,
    pages$,
    mailingLists$,
    customAudienceGroup$,
  ) => {
    return [
      {
        name: 'event_attending',
        readableName: 'EVENT - ATTENDING',
        data: events$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'event_following',
        readableName: 'EVENT - FOLLOWING',
        data: events$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'event_main_music_style_liked',
        readableName: 'EVENT - MAIN MUSIC STYLE',
        hasDistance: true,
        data: events$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'event_secondary_music_styles_liked',
        readableName: 'EVENT - SECONDARY MUSIC STYLES',
        hasDistance: true,
        data: events$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'event_artist_following',
        readableName: 'EVENT - ARTISTS',
        hasDistance: true,
        data: events$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'event_users_in_area',
        readableName: 'EVENT - USERS IN AREA',
        hasDistance: true,
        data: events$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'event_group_following',
        readableName: 'EVENT GROUP - FOLLOWING',
        data: eventGroups$,
        selectValues: ['name', 'city', 'thumbnail'],
      },
      {
        name: 'win_game_played',
        readableName: 'WIN GAME - PLAYED',
        data: winGames$,
        selectValues: ['title', null, 'thumbnail'],
      },
      {
        name: 'win_game_won',
        readableName: 'WIN GAME - WON',
        data: winGames$,
        selectValues: ['title', null, 'thumbnail'],
      },
      {
        name: 'product_transaction_uncompleted',
        readableName: 'PRODUCT - UNCOMPLETED TRANSACTION',
        data: products$,
        selectValues: ['title'],
      },
      {
        name: 'product_transaction_completed',
        readableName: 'PRODUCT - COMPLETED TRANSACTION',
        data: products$,
        selectValues: ['title'],
      },
      {
        name: 'profile',
        readableName: 'PROFILE',
        data: profiles$,
        selectValues: ['name', null, 'thumbnail'],
      },
      {
        name: 'country',
        readableName: 'COUNTRY',
        data: of([
          {name: 'NL', id: 'NL'},
          {name: 'BE', id: 'BE'},
          {name: 'DE', id: 'DE'},
          {name: 'US', id: 'US'},
          {name: 'GB', id: 'GB'},
          {name: 'FR', id: 'FR'},
          {name: 'ES', id: 'ES'},
          {name: 'PL', id: 'PL'},
        ]),
        selectValues: ['name'],
      },
      {
        name: 'age',
        readableName: 'AGE',
        ageRange: true,
      },
      {
        name: 'page_following',
        readableName: 'PAGE - FOLLOWING',
        data: pages$,
        pageSelection: true,
        selectValues: ['name', null, 'thumbnail'],
      },
      {
        name: 'mailing_list',
        readableName: 'MAILING LIST',
        data: mailingLists$,
        selectValues: ['name'],
      },
      {
        name: 'csv_file',
        readableName: 'CSV FILE (csv with user ids)',
        file: true,
      },
      {
        name: 'custom_audience_group',
        readableName: 'Custom Audience Group',
        data: customAudienceGroup$,
        selectValues: ['name'],
      },
      {
        name: 'gender',
        readableName: 'Gender',
        data: of([
          {name: 'Male', id: 1},
          {name: 'Female', id: 2},
        ]),
        selectValues: ['name'],
      },
    ];
  };

  async onFileUpload(event) {
    const file: File = event;
    if (!file) {
      return;
    }

    try {
      const res: UploadedFile = await toPromiseOnce(this.targetAudienceService.uploadCsv(file));
      return res;
    } catch (err) {
      return err;
    }
  }

  checkIfEmpty(obj) {
    return Object.entries(obj).length !== 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

enum AudienceType {
  Event = 'event',
  EventGroup = 'event_group',
  Profile = 'profile',
  Product = 'product',
  WinGame = 'win_game',
  Page = 'page_following',
  MailingList = 'mailing_list',
  CustomGroup = 'custom_audience_group',
}
